<template>
  <div class="wxpay-sub-merchant-apply">
    <div class="top-header">
      <el-button icon="el-icon-arrow-left" @click="goBack">返回</el-button>
      <h2>特约商户进件申请</h2>
      <div class="header-actions">
        <el-button type="primary" @click="handleSubmit" :loading="submitting" :disabled="submitDisabled">
          {{ submitting ? '提交中...' : '提交审核' }}
        </el-button>
      </div>
    </div>

    <!-- ===== 状态提示 ===== -->
    <el-alert v-if="applyStatus > 1" :title="'进件状态: ' + statusText" :type="statusType" :closable="false" show-icon style="margin-bottom:20px">
      <div v-if="applyStatus === 2" style="margin-top:8px;color:#e6a23c">
        已提交，等待代理审核
      </div>
      <div v-if="applyStatus === 3" style="margin-top:8px;color:#409eff">
        初审通过，已提交微信支付审核，请耐心等待审核结果
      </div>
      <div v-if="applyStatus === 4" style="margin-top:8px;color:#67c23a">
        微信审核已通过，进件完成
      </div>
      <div v-if="showRejectReason" style="margin-top:8px;color:#f56c6c">
        驳回原因: {{ rejectReason }}
      </div>
    </el-alert>

    <!-- ===== ⭐ 错误提示区域 ===== -->
    <el-alert
        v-if="submitError && submitError.fields && submitError.fields.length > 0"
        title="提交失败，请完善以下信息"
        type="error"
        :closable="true"
        show-icon
        style="margin-bottom:20px"
        @close="submitError = null"
    >
      <div style="margin-top:6px">
        <div v-for="(field, index) in submitError.fields" :key="index" style="padding:2px 0; font-size:13px">
          • {{ field }}
        </div>
      </div>
      <div v-if="submitError.message && !submitError.fields.length" style="margin-top:6px;font-size:13px">
        {{ submitError.message }}
      </div>
    </el-alert>

    <el-alert v-if="applyStatus === 1 && applyNo" title="草稿状态" description="请完善信息后提交审核" type="info" :closable="false" show-icon style="margin-bottom:20px" />

    <div class="module-cards">
      <div class="module-card" v-for="item in modules" :key="item.key" @click="openModule(item.key)">
        <div class="card-icon" :style="{background: item.color}"><i :class="item.icon" /></div>
        <div class="card-content">
          <div class="card-title">{{ item.title }}</div>
          <div class="card-desc">{{ item.desc }}</div>
        </div>
        <div class="card-status">
          <i v-if="moduleStatus[item.key] === 'done'" class="el-icon-circle-check status-done" />
          <i v-else-if="moduleStatus[item.key] === 'partial'" class="el-icon-warning status-partial" />
          <i v-else class="el-icon-circle-close status-empty" />
        </div>
      </div>
    </div>

    <!-- ===== 各模块弹窗 ===== -->
    <el-dialog :title="dialogTitles[currentModule]" :visible.sync="dialogVisible" width="750px" :close-on-click-modal="false" append-to-body @close="onDialogClose">
      <SubjectInfo
          v-if="currentModule === 'subject'"
          ref="moduleForm"
          :key="'subject-' + (id || 'new')"
          :data="subjectData"
          :applyId="id"
          :applyNo="applyNo"
          :merchantId="merchantId"
          @update="onSubjectUpdate"
          @upload="openImageSelector"
          @saved="onModuleSaved"
          @error="onModuleError"
      />
      <BusinessInfo
          v-if="currentModule === 'business'"
          ref="moduleForm"
          :key="'business-' + (id || 'new')"
          :data="businessData"
          :applyId="id"
          :applyNo="applyNo"
          :merchantId="merchantId"
          @update="onBusinessUpdate"
          @upload="openImageSelector"
          @saved="onModuleSaved"
          @error="onModuleError"
      />
      <IndustryInfo
          v-if="currentModule === 'industry'"
          ref="moduleForm"
          :key="'industry-' + (id || 'new')"
          :data="industryData"
          :applyId="id"
          :applyNo="applyNo"
          :merchantId="merchantId"
          @update="onIndustryUpdate"
          @upload="openImageSelector"
          @saved="onModuleSaved"
          @error="onModuleError"
      />
      <SettlementInfo
          v-if="currentModule === 'settlement'"
          ref="moduleForm"
          :key="'settlement-' + (id || 'new')"
          :data="settlementData"
          :applyId="id"
          :applyNo="applyNo"
          :merchantId="merchantId"
          @update="onSettlementUpdate"
          @saved="onModuleSaved"
          @error="onModuleError"
      />
      <AdminInfo
          v-if="currentModule === 'admin'"
          ref="moduleForm"
          :key="'admin-' + (id || 'new')"
          :data="adminData"
          :applyId="id"
          :applyNo="applyNo"
          :merchantId="merchantId"
          @update="onAdminUpdate"
          @upload="openImageSelector"
          @saved="onModuleSaved"
          @error="onModuleError"
      />
    </el-dialog>

    <image-selector v-model="imageSelectorVisible" :max-count="1" @select="handleImageSelect" />
    <image-uploader :visible.sync="imageUploader.visible" @upload="handleImageUploadComplete" :category="imageUploader.category" v-if="imageUploader.visible" />
  </div>
</template>

<script>
import api from '@/utils/api'
import SubjectInfo from './modules/SubjectInfo.vue'
import BusinessInfo from './modules/BusinessInfo.vue'
import IndustryInfo from './modules/IndustryInfo.vue'
import SettlementInfo from './modules/SettlementInfo.vue'
import AdminInfo from './modules/AdminInfo.vue'
import ImageSelector from '@/components/ImageSelector'
import ImageUploader from '@/components/ImageUploader'

const STATUS_MAP = { 1: '草稿', 2: '已提交', 3: '审核中', 4: '已通过', 5: '已驳回', 6: '已撤销' }
const STATUS_TYPE = { 1: 'info', 2: 'warning', 3: 'warning', 4: 'success', 5: 'danger', 6: 'info' }
const MODULE_KEYS = ['subject', 'business', 'industry', 'settlement', 'admin']

const FIELD_MAP = {
  licenseCopy: { urlField: 'licenseCopyUrl', imageIdField: 'licenseCopyImageId' },
  idCardFront: { urlField: 'idCardFrontUrl', imageIdField: 'idCardFrontImageId' },
  idCardBack: { urlField: 'idCardBackUrl', imageIdField: 'idCardBackImageId' },
  storeEntrance: { urlField: 'storeEntranceUrl', imageIdField: 'storeEntranceImageId' },
  storeInterior: { urlField: 'storeInteriorUrl', imageIdField: 'storeInteriorImageId' },
  authorizationLetter: { urlField: 'authorizationLetterUrl', imageIdField: 'authorizationLetterImageId' },
  qualification: { urlField: 'qualificationUrls', imageIdField: 'qualificationImageId' }
}

export default {
  name: 'WxpaySubMerchantApply',
  components: { SubjectInfo, BusinessInfo, IndustryInfo, SettlementInfo, AdminInfo, ImageSelector, ImageUploader },
  data() {
    return {
      loading: false, submitting: false,
      dialogVisible: false, currentModule: '',
      imageSelectorVisible: false, uploadField: '',
      imageUploader: { visible: false, category: null },

      submitError: null,

      // 公共标识
      id: null, applyNo: '', merchantId: null, applyStatus: 1, rejectReason: '',

      // 各模块数据
      subjectData: {},
      businessData: {},
      industryData: {},
      settlementData: {},
      adminData: {},

      modules: [
        { key: 'subject', title: '主体信息', desc: '营业执照、法人证件等', icon: 'el-icon-user-solid', color: '#409eff' },
        { key: 'business', title: '经营信息', desc: '商户简称、经营场景等', icon: 'el-icon-shop', color: '#67c23a' },
        { key: 'industry', title: '行业资质', desc: '经营类目、费率等', icon: 'el-icon-c-scale-to-original', color: '#e6a23c' },
        { key: 'settlement', title: '结算账户', desc: '开户银行、账号等', icon: 'el-icon-bank-card', color: '#f56c6c' },
        { key: 'admin', title: '管理员信息', desc: '超级管理员姓名、手机等', icon: 'el-icon-s-custom', color: '#909399' }
      ],
      dialogTitles: { subject: '主体信息', business: '经营信息', industry: '行业资质', settlement: '结算账户', admin: '管理员信息' },
      moduleStatus: { subject: '', business: '', industry: '', settlement: '', admin: '' }
    }
  },
  computed: {
    statusText() { return STATUS_MAP[this.applyStatus] || '草稿' },
    statusType() { return STATUS_TYPE[this.applyStatus] || 'info' },
    submitDisabled() { return [2, 3, 4, 6].includes(this.applyStatus) },
    showRejectReason() {
      return this.rejectReason && !MODULE_KEYS.includes(this.rejectReason)
    }
  },
  mounted() {
    console.log('========== [WxpaySubMerchantApply] 组件挂载 ==========')
    const { applyNo, merchantId } = this.$route.query
    console.log('[WxpaySubMerchantApply] 路由参数:', { applyNo, merchantId })

    if (applyNo) {
      this.loadData({ applyNo })
    } else {
      this.initNewApply()
    }
    this.loadImageCategories()
  },
  methods: {
    goBack() {
      console.log('[WxpaySubMerchantApply] 返回签约管理')
      this.$router.push('/Index/Agent/ContractManagement')
    },

    tsToDate(ts) {
      if (!ts || ts === 0) return ''
      const d = new Date(ts)
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },

    convertSubjectType(type) {
      const map = {
        'SUBJECT_TYPE_ENTERPRISE': 'ENTERPRISE',
        'SUBJECT_TYPE_INDIVIDUAL': 'INDIVIDUAL'
      }
      return map[type] || type || ''
    },

    // ==================== 初始化 ====================
    async initNewApply() {
      try {
        const res = await api.login_getAdminInfo()
        const d = (res.data || res)?.data
        console.log('[WxpaySubMerchantApply] 获取到管理员信息:', {
          merchantId: d?.merchantId,
          adminId: d?.id,
          name: d?.name
        })

        if (d?.merchantId) {
          this.merchantId = d.merchantId
          await this.loadData({
            merchantId: this.merchantId,
            createAdminId: d.id
          })
        } else {
          console.warn('[WxpaySubMerchantApply] 未获取到 merchantId')
        }
      } catch (e) {
        console.error('[WxpaySubMerchantApply] 获取管理员信息失败:', e)
      }
    },

    // ==================== 加载草稿数据 ====================
    async loadData(params) {
      this.loading = true
      console.log('[WxpaySubMerchantApply] 开始加载草稿数据, 参数:', params)

      const loadParams = {}
      if (params.applyNo) loadParams.applyNo = params.applyNo
      if (params.merchantId) loadParams.merchantId = params.merchantId
      if (params.createAdminId) loadParams.createAdminId = params.createAdminId

      if (!loadParams.applyNo && loadParams.merchantId && !loadParams.createAdminId) {
        try {
          const adminRes = await api.login_getAdminInfo()
          const adminData = (adminRes.data || adminRes)?.data
          if (adminData?.id) {
            loadParams.createAdminId = adminData.id
            console.log('[WxpaySubMerchantApply] 自动获取 createAdminId:', loadParams.createAdminId)
          }
        } catch (e) {
          console.error('[WxpaySubMerchantApply] 获取 createAdminId 失败:', e)
        }
      }

      if (!loadParams.applyNo && !loadParams.merchantId) {
        console.log('[WxpaySubMerchantApply] 无有效参数，跳过加载')
        this.loading = false
        return
      }

      console.log('[WxpaySubMerchantApply] 最终查询参数:', loadParams)

      try {
        const res = await api.subMerchant_getMyApply(loadParams)
        console.log('[WxpaySubMerchantApply] 草稿数据响应:', res)

        if (res?.data?.success && res.data.data) {
          const d = res.data.data
          console.log('[WxpaySubMerchantApply] 草稿原始数据:', JSON.stringify(d, null, 2))

          this.id = d.id
          this.applyNo = d.applyNo
          this.merchantId = d.merchantId
          this.applyStatus = d.applyStatus
          this.rejectReason = d.rejectReason

          this.subjectData = {}
          this.businessData = {}
          this.industryData = {}
          this.settlementData = {}
          this.adminData = {}

          await this.$nextTick()

          // 主体模块
          this.subjectData = {
            subjectType: this.convertSubjectType(d.subjectType),
            merchantName: d.merchantName || '',
            legalPersonName: d.legalPersonName || '',
            legalPersonIdCard: d.legalPersonIdCard || '',
            licenseNumber: d.licenseNumber || '',
            licenseCopyMediaId: d.licenseCopyMediaId || '',
            licenseCopyImageId: d.licenseCopyImageId,
            licenseCopyUrl: '',
            licenseAddress: d.licenseAddress || '',
            licensePeriodBegin: this.tsToDate(d.licensePeriodBegin),
            licensePeriodEnd: this.tsToDate(d.licensePeriodEnd),
            idCardFrontMediaId: d.idCardFrontMediaId || '',
            idCardFrontImageId: d.idCardFrontImageId,
            idCardFrontUrl: '',
            idCardBackMediaId: d.idCardBackMediaId || '',
            idCardBackImageId: d.idCardBackImageId,
            idCardBackUrl: '',
            idCardPeriodBegin: this.tsToDate(d.idCardPeriodBegin),
            idCardPeriodEnd: this.tsToDate(d.idCardPeriodEnd),
          }

          // 经营模块
          this.businessData = {
            merchantShortName: d.merchantShortName || '',
            servicePhone: d.servicePhone || '',
            salesSceneType: d.salesSceneType || '',
            businessDesc: d.businessDesc || '',
            bizAddressCode: d.bizAddressCode || '',
            storeEntranceMediaId: d.storeEntranceMediaId || '',
            storeEntranceImageId: d.storeEntranceImageId,
            storeEntranceUrl: '',
            storeInteriorMediaId: d.storeInteriorMediaId || '',
            storeInteriorImageId: d.storeInteriorImageId,
            storeInteriorUrl: '',
          }

          // 行业模块
          this.industryData = {
            industryCategory: d.industryCategory || '',
            settlementId: d.settlementId || '',
            standardRate: d.standardRate || '',
            hasPromotion: d.hasPromotion || 0,
            promotionId: d.promotionId || '',
            promotionRate: d.promotionRate || '',
            debitRate: d.debitRate || '',
            creditRate: d.creditRate || '',
            qualificationUrls: '',
            qualificationImageId: d.qualificationImageId,
            qualificationMediaId: d.qualificationMediaId || '',
            additionDesc: d.additionDesc || '',
          }

          // 结算模块
          this.settlementData = {
            bankAccountType: d.bankAccountType || '',
            accountName: d.accountName || '',
            accountBank: d.accountBank || '',
            bankAddressCode: d.bankAddressCode || '',
            bankBranchId: d.bankBranchId || '',
            bankName: d.bankName || '',
            accountNumber: d.accountNumber || '',
          }

          // 管理员模块
          this.adminData = {
            adminType: d.adminType || 'LEGAL',
            adminName: d.adminName || '',
            adminPhone: d.adminPhone || '',
            adminEmail: d.adminEmail || '',
            contactPhone: d.contactPhone || '',
            contactEmail: d.contactEmail || '',
            contactIdDocType: d.contactIdDocType || 'ID_CARD',
            contactIdNumber: d.contactIdNumber || '',
            idCardFrontMediaId: d.contactIdDocCopyMediaId || d.idCardFrontMediaId || '',
            idCardFrontImageId: d.contactIdDocCopyImageId !== undefined ? d.contactIdDocCopyImageId : d.idCardFrontImageId,
            idCardFrontUrl: '',
            idCardBackMediaId: d.contactIdDocCopyBackMediaId || d.idCardBackMediaId || '',
            idCardBackImageId: d.contactIdDocCopyBackImageId !== undefined ? d.contactIdDocCopyBackImageId : d.idCardBackImageId,
            idCardBackUrl: '',
            idCardPeriodBegin: d.contactPeriodBegin ? this.tsToDate(d.contactPeriodBegin) : (d.idCardPeriodBegin ? this.tsToDate(d.idCardPeriodBegin) : ''),
            idCardPeriodEnd: d.contactPeriodEnd ? this.tsToDate(d.contactPeriodEnd) : (d.idCardPeriodEnd ? this.tsToDate(d.idCardPeriodEnd) : ''),
            authorizationLetterMediaId: d.authorizationLetterMediaId || '',
            authorizationLetterImageId: d.authorizationLetterImageId,
            authorizationLetterUrl: '',
          }

          console.log('[WxpaySubMerchantApply] 加载后的 adminData:')
          console.log('  contactIdNumber:', this.adminData.contactIdNumber)
          console.log('  idCardFrontImageId:', this.adminData.idCardFrontImageId)
          console.log('  idCardBackImageId:', this.adminData.idCardBackImageId)
          console.log('  authorizationLetterImageId:', this.adminData.authorizationLetterImageId)

          await this.loadAllPreviewUrls()
          this.checkAllModuleStatus()
          this.$message.success('数据加载成功')
        } else {
          console.log('[WxpaySubMerchantApply] 未找到草稿数据，进入新建模式')
          if (params.merchantId) {
            this.merchantId = parseInt(params.merchantId)
          }
        }
      } catch (e) {
        console.error('[WxpaySubMerchantApply] 加载数据失败:', e)
        if (params.merchantId) {
          this.merchantId = parseInt(params.merchantId)
        }
      } finally {
        this.loading = false
      }
    },

    // ==================== 加载图片预览URL ====================
    async loadAllPreviewUrls() {
      console.log('[WxpaySubMerchantApply] 开始加载图片预览URL')
      const modules = [
        { name: 'subject', data: this.subjectData, fields: ['licenseCopy', 'idCardFront', 'idCardBack'] },
        { name: 'business', data: this.businessData, fields: ['storeEntrance', 'storeInterior'] },
        { name: 'industry', data: this.industryData, fields: ['qualification'] },
        { name: 'admin', data: this.adminData, fields: ['idCardFront', 'idCardBack', 'authorizationLetter'] }
      ]

      for (const mod of modules) {
        for (const f of mod.fields) {
          const map = FIELD_MAP[f]
          if (!map) continue
          const imageId = mod.data[map.imageIdField]
          const urlField = map.urlField
          if (imageId && !mod.data[urlField]) {
            try {
              const res = await api.image_getImageById({ id: imageId })
              if (res?.data?.success && res.data.data?.url) {
                this.$set(mod.data, urlField, res.data.data.url)
                console.log(`[WxpaySubMerchantApply] 加载图片成功: ${mod.name}.${f} -> ${res.data.data.url}`)
              }
            } catch (e) {
              console.error(`[WxpaySubMerchantApply] 加载图片失败: ${mod.name}.${f}`, e)
            }
          }
        }
      }
    },

    async loadImageCategories() {
      try {
        const res = await api.image_getImageCategoryList({})
        if (res.data.code === '000' && res.data.data?.length > 0) {
          this.imageUploader.category = res.data.data[0]
        }
      } catch (e) {
        console.error('[WxpaySubMerchantApply] 加载图片分类失败:', e)
      }
    },

    // ==================== 模块更新回调 ====================
    onSubjectUpdate(data) { this.subjectData = { ...this.subjectData, ...data } },
    onBusinessUpdate(data) { this.businessData = { ...this.businessData, ...data } },
    onIndustryUpdate(data) { this.industryData = { ...this.industryData, ...data } },
    onSettlementUpdate(data) { this.settlementData = { ...this.settlementData, ...data } },
    onAdminUpdate(data) {
      console.log('[WxpaySubMerchantApply] onAdminUpdate 接收数据:', data)
      this.adminData = { ...this.adminData, ...data }
      console.log('[WxpaySubMerchantApply] 更新后的 adminData:')
      console.log('  idCardFrontImageId:', this.adminData.idCardFrontImageId)
      console.log('  idCardBackImageId:', this.adminData.idCardBackImageId)
    },

    // ==================== ⭐ 模块错误回调 ====================
    onModuleError(error) {
      console.log('[WxpaySubMerchantApply] 模块错误:', error)
      if (error && error.fields && error.fields.length > 0) {
        this.submitError = {
          fields: error.fields,
          message: error.message
        }
        this.$nextTick(() => {
          const alertEl = document.querySelector('.el-alert--error')
          if (alertEl) {
            alertEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
        this.$message.warning('请完善: ' + error.fields.join('、'))
      } else if (error && error.message) {
        this.submitError = {
          fields: [],
          message: error.message
        }
        this.$message.error(error.message)
      }
    },

    // ==================== 模块操作 ====================
    openModule(module) {
      console.log(`[WxpaySubMerchantApply] 打开模块: ${module}`)
      this.currentModule = module
      this.dialogVisible = true
      this.submitError = null
    },

    onDialogClose() {
      console.log(`[WxpaySubMerchantApply] 关闭模块: ${this.currentModule}`)
      this.checkAllModuleStatus()
    },

    openImageSelector(field) {
      console.log(`[WxpaySubMerchantApply] 打开图片选择器: ${field}`)
      this.uploadField = field
      this.imageSelectorVisible = true
    },

    async handleImageSelect(images) {
      if (images?.length > 0) {
        const image = images[0]
        const map = FIELD_MAP[this.uploadField]
        if (!map) return
        const mod = this.getCurrentModuleData()
        if (mod) {
          this.$set(mod, map.urlField, image.url)
          this.$set(mod, map.imageIdField, image.id)
          console.log(`[WxpaySubMerchantApply] 图片已选择: ${this.uploadField} -> ${image.url}`)
        }
      }
    },

    async handleImageUploadComplete(image) {
      if (image?.id && image?.url) {
        const map = FIELD_MAP[this.uploadField]
        if (!map) return
        const mod = this.getCurrentModuleData()
        if (mod) {
          this.$set(mod, map.urlField, image.url)
          this.$set(mod, map.imageIdField, image.id)
          console.log(`[WxpaySubMerchantApply] 图片已上传: ${this.uploadField} -> ${image.url}`)
        }
      }
    },

    getCurrentModuleData() {
      const map = {
        subject: this.subjectData,
        business: this.businessData,
        industry: this.industryData,
        settlement: this.settlementData,
        admin: this.adminData
      }
      return map[this.currentModule]
    },

    // ==================== 模块保存回调 ====================
    onModuleSaved(data) {
      console.log('[WxpaySubMerchantApply] 模块保存成功, 返回数据:', data)
      this.submitError = null
      if (data) {
        if (data.id) this.id = data.id
        if (data.applyNo) this.applyNo = data.applyNo
        if (data.merchantId) this.merchantId = data.merchantId
        this.$message.success('保存成功')
        this.dialogVisible = false

        this.loadData({ applyNo: this.applyNo, merchantId: this.merchantId })

        this.checkAllModuleStatus()
        this.$router.replace({
          path: this.$route.path,
          query: { applyNo: this.applyNo, merchantId: this.merchantId }
        }).catch(() => {})
      }
    },

    // ==================== ★★★ 修改：提交审核（不自动查询微信状态）★★★ ====================
    async handleSubmit() {
      console.log('[WxpaySubMerchantApply] 开始提交审核')
      this.submitError = null

      if (!this.id) {
        console.warn('[WxpaySubMerchantApply] 提交失败: 没有ID，请先保存草稿')
        return this.$message.warning('请先保存草稿')
      }

      // 状态为2（已提交）、3（审核中）、4（已通过）、6（已撤销）时禁用提交
      if ([2, 3, 4, 6].includes(this.applyStatus)) {
        return this.$message.warning('当前状态不可重复提交')
      }

      this.checkAllModuleStatus()
      const incompleteModules = []
      for (const [key, status] of Object.entries(this.moduleStatus)) {
        if (status === '' || status === 'partial') {
          const moduleMap = {
            subject: '主体信息',
            business: '经营信息',
            industry: '行业资质',
            settlement: '结算账户',
            admin: '管理员信息'
          }
          incompleteModules.push(moduleMap[key] || key)
        }
      }

      if (incompleteModules.length > 0) {
        this.submitError = {
          fields: incompleteModules.map(m => `请完善「${m}」模块`),
          message: '存在未完善的信息模块'
        }
        this.$message.warning('请完善所有模块后再提交')
        return
      }

      this.submitting = true
      try {
        const res = await api.subMerchant_submitApply({ id: this.id })
        console.log('[WxpaySubMerchantApply] 提交响应:', res)
        if (res?.data?.success) {
          this.applyStatus = 2
          this.$message.success('已提交，等待代理审核')

          // ★★★ 移除自动查询微信状态的代码 ★★★
          // 提交后状态为2（已提交），等待代理审核，不应该查询微信状态

          setTimeout(() => this.$router.push('/Index/Agent/ContractManagement'), 1500)
        } else {
          const msg = res?.data?.message || '提交失败'
          const missingFields = this.extractMissingFields(msg)
          if (missingFields.length > 0) {
            this.submitError = {
              fields: missingFields,
              message: msg
            }
            this.$message.warning('请完善: ' + missingFields.join('、'))
          } else {
            this.$message.error(msg)
          }
        }
      } catch (e) {
        console.error('[WxpaySubMerchantApply] 提交失败:', e)
        const msg = e?.response?.data?.message || e?.message || '提交失败'
        const missingFields = this.extractMissingFields(msg)
        if (missingFields.length > 0) {
          this.submitError = {
            fields: missingFields,
            message: msg
          }
          this.$message.warning('请完善: ' + missingFields.join('、'))
        } else {
          this.$message.error(msg)
        }
      } finally {
        this.submitting = false
      }
    },

    // ==================== 提取缺失字段 ====================
    extractMissingFields(msg) {
      if (!msg) return []
      const fieldMap = {
        '主体类型': '主体类型',
        '营业执照': '营业执照图片',
        '统一社会信用代码': '统一社会信用代码',
        '商户名称': '商户名称',
        '法人姓名': '法人姓名',
        '经营者姓名': '经营者姓名',
        '身份证号': '身份证号',
        '注册地址': '注册地址',
        '营业执照有效期开始': '营业执照有效期开始',
        '营业执照有效期结束': '营业执照有效期结束',
        '身份证正面': '身份证正面',
        '身份证反面': '身份证反面',
        '身份证有效期开始': '身份证有效期开始',
        '身份证有效期结束': '身份证有效期结束',
        '商户简称': '商户简称',
        '客服电话': '客服电话',
        '经营场景': '经营场景',
        '经营描述': '经营描述',
        '所在地区': '所在地区',
        '门店入口图': '门店入口图',
        '店内环境图': '店内环境图',
        '行业类目': '行业类目',
        '结算规则': '结算规则',
        '标准费率': '标准费率',
        '资质图片': '资质图片',
        '账户类型': '账户类型',
        '开户名称': '开户名称',
        '开户银行': '开户银行',
        '开户省份': '开户省份',
        '开户城市': '开户城市',
        '开户支行': '开户支行',
        '银行账号': '银行账号',
        '管理员类型': '管理员类型',
        '管理员姓名': '管理员姓名',
        '管理员手机': '管理员手机号',
        '管理员邮箱': '管理员邮箱',
        '联系手机': '联系手机',
        '联系邮箱': '联系邮箱',
        '经办人证件类型': '经办人证件类型',
        '经办人证件正面': '经办人证件正面',
        '经办人证件反面': '经办人证件反面',
        '经办人证件有效期开始': '经办人证件有效期开始',
        '经办人证件有效期结束': '经办人证件有效期结束',
        '授权函': '授权函'
      }

      const missing = []
      for (const [key, label] of Object.entries(fieldMap)) {
        if (msg.includes(key)) {
          missing.push(label)
        }
      }
      return missing
    },

    // ==================== 模块状态检查 ====================
    checkAllModuleStatus() {
      const s = this.subjectData
      const b = this.businessData
      const i = this.industryData
      const st = this.settlementData
      const a = this.adminData

      this.moduleStatus.subject = s.subjectType && s.merchantName && s.idCardFrontImageId
          ? 'done' : (s.subjectType || s.merchantName ? 'partial' : '')
      this.moduleStatus.business = b.merchantShortName && b.servicePhone && b.salesSceneType
          ? 'done' : (b.merchantShortName || b.salesSceneType ? 'partial' : '')
      this.moduleStatus.industry = i.industryCategory && i.settlementId && i.standardRate
          ? 'done' : (i.industryCategory || i.settlementId ? 'partial' : '')
      this.moduleStatus.settlement = st.accountName && st.accountBank && st.accountNumber
          ? 'done' : (st.accountName || st.accountBank ? 'partial' : '')
      this.moduleStatus.admin = a.adminName && a.adminPhone && a.adminEmail
          ? 'done' : (a.adminName || a.adminPhone ? 'partial' : '')

      console.log('[WxpaySubMerchantApply] 模块状态:', JSON.stringify(this.moduleStatus))
    }
  }
}
</script>

<style scoped>
.wxpay-sub-merchant-apply { max-width:900px; margin:0 auto; padding:20px; min-height:100vh; background:#f5f7fa; }
.top-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; padding:15px 20px; background:#fff; border-radius:8px; box-shadow:0 2px 12px rgba(0,0,0,0.04); }
.top-header h2 { margin:0; font-size:20px; }
.header-actions { display:flex; gap:10px; }
.module-cards { display:flex; flex-direction:column; gap:12px; }
.module-card { display:flex; align-items:center; padding:20px; background:#fff; border-radius:8px; box-shadow:0 2px 12px rgba(0,0,0,0.04); cursor:pointer; transition:transform 0.2s, box-shadow 0.2s; }
.module-card:hover { transform:translateY(-2px); box-shadow:0 4px 16px rgba(0,0,0,0.08); }
.card-icon { width:48px; height:48px; border-radius:12px; display:flex; align-items:center; justify-content:center; color:#fff; font-size:22px; flex-shrink:0; }
.card-content { flex:1; margin-left:16px; }
.card-title { font-size:16px; font-weight:600; color:#303133; }
.card-desc { font-size:13px; color:#909399; margin-top:4px; }
.card-status { flex-shrink:0; margin-left:16px; }
.status-done { color:#67c23a; font-size:24px; }
.status-partial { color:#e6a23c; font-size:24px; }
.status-empty { color:#c0c4cc; font-size:24px; }
</style>